import { CALENDAR_VIEW_TYPE } from 'constants/calendar';
import moment from 'utils/moment';
import { useCallback, useMemo } from 'react';
import { calendarSelectors } from 'services/selectors/apt';
import shopSelectors from 'services/selectors/shop';
import { IDateRangeItem } from 'utils/getRangeDates';
import { getStaffCalendarPage } from 'utils/staff';
import { ANYONE_ID } from './anyone';
import StaffHeader from './StaffHeader';
import { ICalendarHeader, ICalendarItemData, ICalendarSchema } from './types';

const useCalendarFilter = (): ICalendarSchema => {
  const distanceTimeline = calendarSelectors.distanceTimeline();

  const typeView = calendarSelectors.typeView();
  const dateRangeList = calendarSelectors.dateRanges();
  const staffList = shopSelectors.staffsAvailable();
  const salonWorkingTime = shopSelectors.salonWorkingTime();
  const currentPage = calendarSelectors.currentPage();
  const staffGroupsByStaffId = shopSelectors.staffGroupsByStaffId();
  const fullScreen = calendarSelectors.fullScreen();

  const headers = useMemo(() => {
    if (typeView === CALENDAR_VIEW_TYPE.WEEK_VIEW) {
      return dateRangeList.map((item) => {
        const _date = moment(item.value);
        const result: ICalendarHeader = ({
          id: item.id,
          value: _date,
          label: _date.format('DD.MM'),
          type: 'date',
          typeColor: 0,
        });
        return result;
      });
    }

    if (typeView === CALENDAR_VIEW_TYPE.DAY_VIEW) {
      const headers: ICalendarHeader[] = [];
      let i = 1;
      staffList.forEach((item) => {
        if (getStaffCalendarPage(item, fullScreen) !== currentPage) return;
        const result: ICalendarHeader = ({
          id: item.id,
          value: item.id,
          label: item.name,
          type: 'staff',
          url: item.avatar,
          typeColor: i,
          // The Anyone column is a synthetic staff and keeps its plain sticky header.
          groupColor: item.id === ANYONE_ID ? undefined : staffGroupsByStaffId[item.id]?.colorHex,
          workingTime: item?.timeSchedule ?? undefined,
        });
        i++;
        if (i > 6) {
          i = 1;
        }
        headers.push(result);
      });

      return headers;
    }
    return [];
  }, [typeView, dateRangeList, staffList, currentPage, fullScreen, staffGroupsByStaffId]);

  const renderHeader = useCallback((o: ICalendarHeader<any>) => {
    if (typeView === CALENDAR_VIEW_TYPE.WEEK_VIEW)
      return (
        <>
          <p className='subtext'>{o?.value?.format('ddd, MMMM DD')}</p>
        </>
      );

    return (
      <StaffHeader url={o.url} name={o.label} typeColor={o.typeColor} groupColor={o.groupColor} />
    );
  }, [typeView]);

  const filterAppointments = useCallback((order: ICalendarItemData, _col: ICalendarHeader<any>) => {
    if (typeView === CALENDAR_VIEW_TYPE.WEEK_VIEW) {
      const col = _col as ICalendarHeader<IDateRangeItem['value']>;
      return moment(order.startTime).isSame(col?.value, 'day');
    }

    if (typeView === CALENDAR_VIEW_TYPE.DAY_VIEW) {
      const col = _col as ICalendarHeader<any>;
      const staffId = order?.staffId;
      return staffId === col.id;
    }
    return false;
  }, [typeView]);

  return useMemo(() => ({
    distanceTimeline,
    headers,
    typeView,
    renderHeader,
    filterAppointments,
    timeHourEnd: salonWorkingTime.timeHourEnd,
    timeHourStart: salonWorkingTime.timeHourStart,
    timeMinuteEnd: salonWorkingTime.timeMinuteEnd,
    timeMinuteStart: salonWorkingTime.timeMinuteStart,
    fullScreen,
  }), [
    distanceTimeline,
    headers,
    typeView,
    renderHeader,
    filterAppointments,
    salonWorkingTime.timeHourEnd,
    salonWorkingTime.timeHourStart,
    salonWorkingTime.timeMinuteEnd,
    salonWorkingTime.timeMinuteStart,
    fullScreen,
  ]);
};

export default useCalendarFilter;
